/**
 * 「改完回原地」的白名单 —— `returnTo` 的**唯一校验处**（`DR-066` §5 · `AC-B5.10` / `AC-B5.11`）。
 *
 * ## 🔴 为什么需要白名单（而不是直接 `navigate(raw)`）
 *
 * 修前 `NewTransactionPage` 保存成功后**硬跳 `/journal`** ⇒ 从信用卡账单往期改一笔，
 * 回来落在流水页，用户得自己再点回那张卡的**那一期**（负责人 2026-10-28 第 3 条）。
 * 但把 `?returnTo=` 原样交给 `navigate()` 是**开放重定向**：
 * `?returnTo=https://evil.example.com` 会在保存后把用户送出站。
 *
 * ⇒ 只有两个落点在白名单里，其余**一律** `/journal`：
 *
 * | 落点 | 形态 |
 * |---|---|
 * | 流水页 | `/journal`（可带 `?period=` 之外的东西？**不带** —— 见下） |
 * | 信用卡账单页 | `/accounts/<非空 accountId>/bill`，可带 `?period=current\|unbilled\|YYYY-MM-DD` |
 *
 * 🔴 **不接受**：绝对 URL（`https://…`）、协议相对 URL（`//evil`）、反斜杠变体（`/\evil`）、
 * 相对路径（`journal`）、带片段（`#…`）、`..` 段、多层路径（`/accounts/x/bill/../../y`）。
 *
 * ## 为什么 `period` 的合法性也在这里判
 *
 * 同一件事在两处判（这里判一次"能不能进白名单"、`CreditBillPage` 再判一次"这个值认不认"）
 * 就会分叉：白名单放行 `period=abc`、页面当成往期 `abc` 去请求 ⇒ 一次多余的 404。
 * ⇒ 合法 period 的判据只有 `isValidPeriod()` 一处，两处都调它。
 */

import { isRealDayString } from '@jiaying/shared';

/** 兜底落点（流水页）—— 与修前行为**完全一致**，`returnTo` 缺省时零回归 */
export const DEFAULT_RETURN_TO = '/journal';

/** 账单期的三档：`current` / `unbilled` / 往期的**账单日** `YYYY-MM-DD` */
export type BillPeriod = 'current' | 'unbilled' | string;

/**
 * `period` 是否合法（**唯一判据**）。
 *
 * - `current` / `unbilled` 两个字面量；
 * - 或真实的 `YYYY-MM-DD`（`2026-13-01` / `2026-02-30` 都不算 —— 用共享内核 `isRealDayString`，
 *   不用只验形态的 `isDayString`，铁律 99）。
 */
export function isValidPeriod(raw: string | null | undefined): raw is string {
  if (raw === null || raw === undefined || raw === '') return false;
  if (raw === 'current' || raw === 'unbilled') return true;
  return isRealDayString(raw);
}

/** 从任意字符串里取合法 period；非法 ⇒ `'current'`（页面的默认档） */
export function readPeriod(raw: string | null | undefined): BillPeriod {
  return isValidPeriod(raw) ? raw : 'current';
}

/** 白名单路径之一：信用卡账单页 */
const BILL_PATH = /^\/accounts\/([^/?#\\]+)\/bill$/;

/**
 * 把 `?returnTo=` 的原始值**归一成一个可安全 `navigate()` 的站内路径**。
 *
 * 非法 / 缺失 / 危险值 ⇒ {@link DEFAULT_RETURN_TO}（不抛、不警告 —— 用户改了地址栏而已）。
 */
export function safeReturnTo(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return DEFAULT_RETURN_TO;
  const value = raw.trim();
  if (value === '') return DEFAULT_RETURN_TO;

  // ① 必须是站内绝对路径，且排除协议相对 / 反斜杠变体（浏览器会把 `\` 当 `/`）
  if (!value.startsWith('/')) return DEFAULT_RETURN_TO;
  if (value.startsWith('//')) return DEFAULT_RETURN_TO;
  if (value.includes('\\')) return DEFAULT_RETURN_TO;
  // ② 片段一律不接受（`#` 之后的语义与路由无关，且容易夹带奇怪内容）
  if (value.includes('#')) return DEFAULT_RETURN_TO;
  // ③ `..` 段会让正则匹配的"形状"与实际解析出的路径分叉
  if (value.split('/').includes('..')) return DEFAULT_RETURN_TO;

  const qIndex = value.indexOf('?');
  const path = qIndex === -1 ? value : value.slice(0, qIndex);
  const query = qIndex === -1 ? '' : value.slice(qIndex + 1);

  // ④ 流水页：不接受任何查询串（那边的筛选状态是页面内状态，不在 URL 上）
  if (path === DEFAULT_RETURN_TO) return DEFAULT_RETURN_TO;

  // ⑤ 账单页：路径必须是 `/accounts/<id>/bill`
  if (!BILL_PATH.test(path)) return DEFAULT_RETURN_TO;

  // ⑥ 查询串只认 `period`，且值必须合法；非法 ⇒ 退到「本期」（而不是整条作废）
  if (query === '') return path;
  const params = new URLSearchParams(query);
  // 任何多余参数（可能被下游当成别的意思）⇒ 只保留 period，其余丢弃
  const period = params.get('period');
  return isValidPeriod(period) ? `${path}?period=${period}` : path;
}

/**
 * 由账单页当前位置构造 `returnTo`（页面自己拼，**不手写字符串** —— 手写必漏 encode）。
 *
 * 🔴 `period` 只接受合法值；非法 / 缺省 ⇒ 不带查询串（回到该卡的「本期」，与默认一致）。
 */
export function billReturnTo(accountId: string, period: BillPeriod): string {
  const base = `/accounts/${encodeURIComponent(accountId)}/bill`;
  return isValidPeriod(period) && period !== 'current' ? `${base}?period=${period}` : base;
}
